import { Fragment, useState } from "react";
import { ChevronDown, Pencil, Plus } from "lucide-react";

import Layout from "../../components/Layout";
import { Heading } from "@/components/typography/heading";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const VARIANT_GROUPS = [
    {
        label: "Registry contract",
        description:
            "Pinned by other shadcn primitives (AlertDialog, Dialog, Sheet, Calendar, Carousel, Pagination, Sidebar, Combobox, InputGroup). Restyling these changes those components too.",
        variants: ["default", "outline", "ghost"],
    },
    {
        label: "App / brand",
        description:
            "Not referenced by any registry primitive. Safe to restyle for brand without side effects.",
        variants: ["contrast", "secondary", "destructive", "link"],
    },
] as const;

const SIZES = ["xs", "sm", "default", "lg"] as const;

const ICON_SIZES = ["icon-xs", "icon-sm", "icon", "icon-lg"] as const;

// Registry trio + brand contrast for size matrices
const SIZE_VARIANTS = ["default", "outline", "ghost", "contrast"] as const;

function ColumnLabel({ children }: { children: React.ReactNode }) {
    return <div className="text-sm font-medium text-muted-foreground">{children}</div>;
}

function RowLabel({ children }: { children: React.ReactNode }) {
    return <code className="text-sm">{children}</code>;
}

export default function Buttons() {
    const [graySurface, setGraySurface] = useState(false);

    return (
        <Layout gray={graySurface}>
            <div className="space-y-10 p-4">
                <div>
                    <Heading type="h1" className="mb-2">Buttons</Heading>
                    <p className="mb-4 max-w-2xl text-muted-foreground">
                        Hover, click, and Tab through these to check interaction states — hover,
                        active, and focus rings only render on real interaction.
                    </p>
                    <Button variant="outline" size="sm" onClick={() => setGraySurface(!graySurface)}>
                        Surface: {graySurface ? "gray-100" : "background"}
                    </Button>
                </div>

                <section>
                    <Heading type="h2" className="mb-4">Variants and states</Heading>
                    <div className="grid grid-cols-5 items-center justify-items-start gap-4">
                        <ColumnLabel>Variant</ColumnLabel>
                        <ColumnLabel>Enabled</ColumnLabel>
                        <ColumnLabel>Disabled</ColumnLabel>
                        <ColumnLabel>Popup open</ColumnLabel>
                        <ColumnLabel>With icons</ColumnLabel>

                        {VARIANT_GROUPS.map((group) => (
                            <Fragment key={group.label}>
                                <div className="col-span-5 w-full border-t border-border pt-4">
                                    <Heading type="h5" tag="h3">{group.label}</Heading>
                                    <p className="max-w-3xl text-sm text-muted-foreground">
                                        {group.description}
                                    </p>
                                </div>

                                {group.variants.map((variant) => (
                                    <Fragment key={variant}>
                                        <RowLabel>{variant}</RowLabel>
                                        <Button variant={variant}>Button</Button>
                                        <Button variant={variant} disabled>Button</Button>
                                        <Button variant={variant} aria-haspopup="menu" aria-expanded>
                                            Button
                                            <ChevronDown data-icon="inline-end" />
                                        </Button>
                                        <Button variant={variant}>
                                            <Plus data-icon="inline-start" />
                                            Button
                                            <ChevronDown data-icon="inline-end" />
                                        </Button>
                                    </Fragment>
                                ))}
                            </Fragment>
                        ))}
                    </div>
                </section>

                <section>
                    <Heading type="h2" className="mb-4">Sizes</Heading>
                    <div className="grid grid-cols-5 items-center justify-items-start gap-4">
                        <ColumnLabel>Size</ColumnLabel>
                        {SIZE_VARIANTS.map((variant) => (
                            <ColumnLabel key={variant}>{variant}</ColumnLabel>
                        ))}

                        {SIZES.map((size) => (
                            <Fragment key={size}>
                                <RowLabel>{size}</RowLabel>
                                {SIZE_VARIANTS.map((variant) => (
                                    <Button key={variant} variant={variant} size={size}>
                                        <Plus data-icon="inline-start" />
                                        Book Talent
                                    </Button>
                                ))}
                            </Fragment>
                        ))}
                    </div>
                </section>

                <section>
                    <Heading type="h2" className="mb-4">Icon sizes</Heading>
                    <div className="grid grid-cols-5 items-center justify-items-start gap-4">
                        <ColumnLabel>Size</ColumnLabel>
                        {SIZE_VARIANTS.map((variant) => (
                            <ColumnLabel key={variant}>{variant}</ColumnLabel>
                        ))}

                        {ICON_SIZES.map((size) => (
                            <Fragment key={size}>
                                <RowLabel>{size}</RowLabel>
                                {SIZE_VARIANTS.map((variant) => (
                                    <Button key={variant} variant={variant} size={size} aria-label="edit">
                                        <Pencil />
                                    </Button>
                                ))}
                            </Fragment>
                        ))}
                    </div>
                </section>

                <section>
                    <Heading type="h2" className="mb-2">Disabled next to other primitives</Heading>
                    <p className="mb-4 max-w-2xl text-muted-foreground">
                        <code>default</code> and <code>contrast</code> use a flat muted fill when
                        disabled; every other variant and primitive fades with opacity instead.
                    </p>
                    <div className="grid grid-cols-5 items-center justify-items-start gap-4">
                        <Button disabled>Book Talent</Button>
                        <Button variant="outline" disabled>Book Talent</Button>
                        <Button variant="ghost" disabled>Book Talent</Button>
                        <Button variant="contrast" disabled>Book Talent</Button>
                        <Input disabled defaultValue="Disabled input" />
                    </div>
                </section>
            </div>
        </Layout>
    );
}
